<template>
	<transition name="fade">
		<div
			v-if="internalValue"
			class="loading-overlay"
			:class="{ 'block-mode': !fullscreen }"
			@click.stop
		>
			<div class="loading-container">
				<!-- Loading 狀態：只有動畫卡片 -->
				<div v-if="internalStatus === 'loading'" class="icon-card">
					<div class="icon-wrapper">
						<!-- SVGA Loading -->
						<canvas v-show="!svgaFailed" ref="svgaCanvas" class="svga-canvas" />
						<!-- Fallback: CSS Spinner -->
						<div v-show="svgaFailed" class="loading-spinner" />
					</div>
				</div>

				<!-- 成功/失敗狀態：圖示 + 訊息在卡片內 -->
				<div v-else class="result-card">
					<div class="icon-wrapper">
						<!-- 成功：綠色打勾動畫 -->
						<svg
							v-if="internalStatus === 'success'"
							class="icon-svg success-icon"
							viewBox="0 0 52 52"
						>
							<circle
								class="success-circle"
								cx="26"
								cy="26"
								r="24"
								fill="none"
								stroke="#22c55e"
								stroke-width="2"
							/>
							<path
								class="success-check"
								fill="none"
								stroke="#22c55e"
								stroke-width="3"
								stroke-linecap="round"
								stroke-linejoin="round"
								d="M14 27l7 7 16-16"
							/>
						</svg>

						<!-- 失敗：紅色打叉動畫 -->
						<svg
							v-if="internalStatus === 'error'"
							class="icon-svg error-icon"
							viewBox="0 0 52 52"
						>
							<circle
								class="error-circle"
								cx="26"
								cy="26"
								r="24"
								fill="none"
								stroke="#ef4444"
								stroke-width="2"
							/>
							<path
								class="error-cross-1"
								fill="none"
								stroke="#ef4444"
								stroke-width="3"
								stroke-linecap="round"
								d="M16 16 36 36"
							/>
							<path
								class="error-cross-2"
								fill="none"
								stroke="#ef4444"
								stroke-width="3"
								stroke-linecap="round"
								d="M36 16 16 36"
							/>
						</svg>
					</div>
					<!-- 訊息 -->
					<p class="result-message" :class="internalStatus">
						{{ currentMessage }}
					</p>
					<!-- 重整按鈕（僅在錯誤狀態且允許重整時顯示） -->
					<button
						v-if="internalStatus === 'error' && showRefreshOnError"
						class="refresh-btn"
						@click="handleRefresh"
					>
						<svg
							class="refresh-icon"
							width="16"
							height="16"
							viewBox="0 0 16 16"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M14 8C14 11.3137 11.3137 14 8 14C4.68629 14 2 11.3137 2 8C2 4.68629 4.68629 2 8 2C9.84871 2 11.5 2.82828 12.6 4.2M12.6 4.2V1M12.6 4.2H9.4"
								stroke="white"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
						重新整理
					</button>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import SVGA from 'svgaplayerweb'

// SVGA 快取（模組級，跨組件實例共享）
const svgaCache = new Map()

export default {
	name: 'ByLoadingFeedback',
	props: {
		value: {
			type: Boolean,
			default: false,
		},
		status: {
			type: String,
			default: 'loading',
			validator: function (val) {
				return ['loading', 'success', 'error'].indexOf(val) !== -1
			},
		},
		message: {
			type: String,
			default: '載入中...',
		},
		successText: {
			type: String,
			default: '操作成功',
		},
		errorText: {
			type: String,
			default: '操作失敗',
		},
		// 是否全屏模式，false 時為區塊模式（父容器需設定 position: relative）
		fullscreen: {
			type: Boolean,
			default: true,
		},
		// 延遲顯示時間（毫秒），操作在此時間內完成則不顯示 loading，避免快速操作閃爍
		showDelay: {
			type: Number,
			default: 200,
		},
		// 最短顯示時間（毫秒），一旦顯示後至少維持此時間，避免顯示後立刻消失
		minDuration: {
			type: Number,
			default: 600,
		},
		// 結果狀態顯示時間（毫秒），0 = 不自動關閉
		resultDuration: {
			type: Number,
			default: 1500,
		},
		// SVGA 動畫檔路徑
		svgaSrc: {
			type: String,
			default: '/common_img_loading.svga',
		},
		// 是否顯示成功/失敗結果
		showResult: {
			type: Boolean,
			default: true,
		},
		// 錯誤時是否顯示重整按鈕
		showRefreshOnError: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			internalValue: false,
			internalStatus: 'loading',
			showStartTime: null,
			showDelayTimer: null,
			closeTimer: null,
			resultTimer: null,
			player: null,
			parser: null,
			svgaFailed: false,
		}
	},
	computed: {
		currentMessage: function () {
			const messages = {
				loading: this.message,
				success: this.successText,
				error: this.errorText,
			}
			return messages[this.internalStatus]
		},
	},
	watch: {
		value: {
			handler(newVal) {
				if (newVal) {
					// 清除可能存在的計時器
					this.clearTimers()
					this.internalStatus = this.status

					// 延遲顯示：在 showDelay 內完成的操作不會出現 loading
					if (this.showDelay > 0 && this.status === 'loading') {
						const self = this
						this.showDelayTimer = setTimeout(function () {
							self.showDelayTimer = null
							self.doShow()
						}, this.showDelay)
					} else {
						this.doShow()
					}
				} else {
					this.handleClose()
				}
			},
			immediate: true,
		},
		status: function (val) {
			let self
			// 延遲期間收到結果狀態（success/error），跳過延遲立即顯示結果
			if (this.showDelayTimer && val !== 'loading') {
				clearTimeout(this.showDelayTimer)
				this.showDelayTimer = null
				this.doShow()
			}
			if (!this.internalValue) return

			this.internalStatus = val

			if (val === 'loading') {
				// 清除前一輪的 resultTimer / closeTimer，避免舊 timer 誤殺新 session
				this.clearTimers()
				this.showStartTime = Date.now()
				self = this
				this.$nextTick(function () {
					self.initSvga()
				})
			} else {
				this.stopSvga()
				// 處理結果狀態
				if (!this.showResult) {
					this.doClose()
					return
				}
				// 如果顯示重整按鈕，不自動關閉
				if (val === 'error' && this.showRefreshOnError) {
					return
				}
				// 自動關閉
				if (this.resultDuration > 0) {
					self = this
					this.resultTimer = setTimeout(function () {
						self.doClose()
					}, this.resultDuration)
				}
			}
		},
	},
	methods: {
		doShow: function () {
			this.showStartTime = Date.now()
			this.internalValue = true
			this.internalStatus = this.status
			// 初始化 SVGA
			if (this.internalStatus === 'loading') {
				const self = this
				this.$nextTick(function () {
					self.initSvga()
				})
			}
		},

		initSvga: function () {
			if (!this.$refs.svgaCanvas) return

			const canvas = this.$refs.svgaCanvas
			const self = this
			const cacheKey = this.svgaSrc

			this.svgaFailed = false

			// 先銷毀舊的 player，避免多個 player 疊加在同一個 canvas
			if (this.player) {
				this.player.stopAnimation()
				this.player.clear()
				this.player = null
			}

			this.player = new SVGA.Player(canvas)

			// 檢查快取
			if (svgaCache.has(cacheKey)) {
				const videoItem = svgaCache.get(cacheKey)
				this.playSvga(videoItem)
				return
			}

			// 首次載入
			this.parser = new SVGA.Parser(canvas)
			this.parser.load(
				this.svgaSrc,
				function (videoItem) {
					// 存入快取
					svgaCache.set(cacheKey, videoItem)
					self.playSvga(videoItem)
				},
				function () {
					// SVGA 載入失敗，使用 fallback spinner
					self.svgaFailed = true
				},
			)
		},

		playSvga: function (videoItem) {
			const canvas = this.$refs.svgaCanvas
			if (!canvas) return

			// 取得 SVGA 原始尺寸
			const svgaWidth = videoItem.videoSize.width
			const svgaHeight = videoItem.videoSize.height

			// 設定 canvas 尺寸為 SVGA 原始尺寸
			canvas.width = svgaWidth
			canvas.height = svgaHeight

			this.player.setVideoItem(videoItem)
			this.player.loops = 0
			this.player.startAnimation()
		},

		stopSvga: function () {
			if (this.player) {
				this.player.stopAnimation()
				this.player.clear()
			}
		},

		handleClose: function () {
			// 還在延遲等待中
			if (this.showDelayTimer) {
				clearTimeout(this.showDelayTimer)
				this.showDelayTimer = null
				// 前一輪的 internalValue 仍為 true（closeTimer 被 clearTimers 中斷），走正常關閉流程
				if (this.internalValue) {
					this.doClose()
				} else {
					this.$emit('input', false)
					this.$emit('close')
				}
				return
			}

			if (!this.showStartTime || !this.internalValue) {
				return
			}

			const elapsed = Date.now() - this.showStartTime
			const remaining = this.minDuration - elapsed

			if (remaining > 0) {
				const self = this
				this.closeTimer = setTimeout(function () {
					self.doClose()
				}, remaining)
			} else {
				this.doClose()
			}
		},

		doClose: function () {
			this.stopSvga()
			this.internalValue = false
			this.internalStatus = 'loading'
			this.showStartTime = null
			this.clearTimers()
			this.$emit('input', false)
			this.$emit('close')
		},

		clearTimers: function () {
			if (this.showDelayTimer) {
				clearTimeout(this.showDelayTimer)
				this.showDelayTimer = null
			}
			if (this.closeTimer) {
				clearTimeout(this.closeTimer)
				this.closeTimer = null
			}
			if (this.resultTimer) {
				clearTimeout(this.resultTimer)
				this.resultTimer = null
			}
		},

		handleRefresh: function () {
			window.location.reload()
		},
	},
	beforeDestroy: function () {
		this.stopSvga()
		this.clearTimers()
	},
}
</script>

<style scoped>
/* 遮罩層 - 淡色半透明 + 模糊 */
.loading-overlay {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	justify-content: center;
	align-items: center;
	background-color: rgba(0, 0, 0, 0.2);
	backdrop-filter: blur(10px);
	-webkit-backdrop-filter: blur(10px);
	z-index: 9999;
}

/* 區塊模式 */
.loading-overlay.block-mode {
	position: absolute;
	z-index: 999;
}

/* 整體容器 */
.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
}

/* Loading 狀態的白色卡片 */
.icon-card {
	background: #ffffff;
	border-radius: 16px;
	padding: 16px;
	box-shadow:
		0 0 24px rgba(41, 75, 112, 0.35),
		0 0 48px rgba(41, 75, 112, 0.2),
		0 8px 32px rgba(0, 0, 0, 0.12);
}

/* 成功/失敗狀態的白色卡片 */
.result-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	background: #ffffff;
	border-radius: 16px;
	padding: 24px 32px;
	gap: 16px;
	box-shadow:
		0 0 24px rgba(41, 75, 112, 0.35),
		0 0 48px rgba(41, 75, 112, 0.2),
		0 8px 32px rgba(0, 0, 0, 0.12);
	max-width: calc(100vw - 48px);
	width: 320px;
	box-sizing: border-box;
}

/* 圖標區域 */
.icon-wrapper {
	width: 140px;
	height: 140px;
	display: flex;
	justify-content: center;
	align-items: center;
	overflow: hidden;
}

.svga-canvas {
	transform: scale(0.35);
}

/* Fallback CSS Spinner */
.loading-spinner {
	width: 48px;
	height: 48px;
	border: 4px solid #e5e7eb;
	border-top-color: #294b70;
	border-radius: 50%;
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.icon-svg {
	width: 80px;
	height: 80px;
}

/* 結果訊息文字 */
.result-message {
	font-size: 15px;
	font-weight: 500;
	margin: 0;
	text-align: center;
	letter-spacing: 0.5px;
	color: #374151;
	word-break: break-word;
	line-height: 1.5;
}

.result-message.success {
	color: #16a34a;
}

.result-message.error {
	color: #dc2626;
}

/* 重整按鈕 */
.refresh-btn {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 12px 24px;
	background: #294b70;
	color: white;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.3s ease;
	box-shadow: 0 2px 8px rgba(41, 75, 112, 0.3);
}

.refresh-btn:hover {
	background: #1e3a5f;
	box-shadow: 0 4px 12px rgba(41, 75, 112, 0.4);
	transform: translateY(-1px);
}

.refresh-btn:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(41, 75, 112, 0.3);
}

.refresh-icon {
	display: block;
}

/* ==================== */
/* 成功打勾動畫 */
/* ==================== */

.success-circle {
	stroke-dasharray: 166;
	stroke-dashoffset: 166;
	animation: strokeCircle 0.5s ease-out forwards;
}

.success-check {
	stroke-dasharray: 48;
	stroke-dashoffset: 48;
	animation: strokeCheck 0.35s ease-out 0.4s forwards;
}

@keyframes strokeCircle {
	to {
		stroke-dashoffset: 0;
	}
}

@keyframes strokeCheck {
	to {
		stroke-dashoffset: 0;
	}
}

/* ==================== */
/* 失敗打叉動畫 */
/* ==================== */

.error-circle {
	stroke-dasharray: 166;
	stroke-dashoffset: 166;
	animation: strokeCircle 0.5s ease-out forwards;
}

.error-cross-1 {
	stroke-dasharray: 30;
	stroke-dashoffset: 30;
	animation: strokeCross 0.25s ease-out 0.4s forwards;
}

.error-cross-2 {
	stroke-dasharray: 30;
	stroke-dashoffset: 30;
	animation: strokeCross 0.25s ease-out 0.55s forwards;
}

@keyframes strokeCross {
	to {
		stroke-dashoffset: 0;
	}
}

/* ==================== */
/* 淡入淡出 */
/* ==================== */

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

.fade-enter-active .loading-container {
	animation: scaleIn 0.25s ease-out;
}

.fade-leave-active .loading-container {
	animation: scaleOut 0.2s ease-in;
}

@keyframes scaleIn {
	from {
		transform: scale(0.85);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes scaleOut {
	from {
		transform: scale(1);
		opacity: 1;
	}
	to {
		transform: scale(0.85);
		opacity: 0;
	}
}
</style>
